/**
 * Verity Design Tokens — single source of truth for all design values.
 *
 * Consumed by:
 *   - theme.ts (Mantine theme config)
 *   - main.tsx cssVariablesResolver (generates --verity-xxx CSS custom properties)
 *
 * Components access tokens via `var(--verity-xxx)` CSS custom properties,
 * NOT by importing this file directly.
 */

import type { MantineColorsTuple } from "@mantine/core";

// ─── Color Palettes ──────────────────────────────────────────────────────────

export const palette = {
  gray: [
    "#FDFDFD", // 0 – near white
    "#F3F4F6", // 1 – light gray
    "#E5E7EB", // 2 – soft gray
    "#D1D5DB", // 3 – neutral gray
    "#9CA3AF", // 4 – medium gray
    "#6B7280", // 5 – slate gray
    "#4B5563", // 6 – dark gray
    "#374151", // 7 – darker gray
    "#1F2937", // 8 – near black
    "#111827", // 9 – deep dark
  ] satisfies MantineColorsTuple,

  blue: [
    "#EEF2FC", // 0 – lightest tint
    "#DEE5FA", // 1 – light tint
    "#B8C7F3", // 2 – soft blue
    "#8FA6EB", // 3 – medium-light
    "#6785E2", // 4 – medium
    "#4A6BDB", // 5 – slightly lighter than primary
    "#3455D3", // 6 – primary (brand blue)
    "#2B47B3", // 7 – darker
    "#233A94", // 8 – deep blue
    "#1B2D75", // 9 – darkest
  ] satisfies MantineColorsTuple,

  darkGray: [
    "#DCDCDC", // 0 – primary text (light on dark)
    "#BCBCBC", // 1 – secondary text
    "#A0A0A0", // 2 – muted text
    "#6B7280", // 3 – dimmed
    "#4B5563", // 4 – border
    "#374151", // 5 – card bg hover
    "#2D2D2D", // 6 – button bg
    "#1F1F1F", // 7 – card bg
    "#161616", // 8 – sidebar bg
    "#101010", // 9 – main bg
  ] satisfies MantineColorsTuple,

  // Semantic palettes for Callouts, validation badges, status pills.
  // Index 1 is the light tint used as a callout background; index 6 is
  // the saturated primary used for the icon + 1px left strip; indices
  // 7–9 are dark-mode variants. The flat scalars `errorColor` /
  // `successColor` / `warningColor` re-derive from index 6 below.
  error: [
    "#fef2f2", // 0 – very light tint
    "#fee2e2", // 1 – light tint (callout bg)
    "#fecaca", // 2
    "#fca5a5", // 3
    "#f87171", // 4
    "#f47171", // 5
    "#ef4444", // 6 – primary red (matches existing errorColor)
    "#dc2626", // 7
    "#b91c1c", // 8
    "#991b1b", // 9
  ] satisfies MantineColorsTuple,

  success: [
    "#f0fdf4", // 0
    "#dcfce7", // 1 – callout bg
    "#bbf7d0", // 2
    "#86efac", // 3
    "#4ade80", // 4
    "#34c460", // 5
    "#22c55e", // 6 – primary green (matches existing successColor)
    "#16a34a", // 7
    "#15803d", // 8
    "#166534", // 9
  ] satisfies MantineColorsTuple,

  warning: [
    "#fffbeb", // 0
    "#fef3c7", // 1 – callout bg
    "#fde68a", // 2
    "#fcd34d", // 3
    "#fbbf24", // 4
    "#f8a91a", // 5
    "#f59e0b", // 6 – primary amber (matches existing warningColor)
    "#d97706", // 7
    "#b45309", // 8
    "#92400e", // 9
  ] satisfies MantineColorsTuple,
} as const;

// ─── Semantic Colors (per color scheme) ──────────────────────────────────────

export const semanticColors = {
  light: {
    surfaceBg: "#ffffff",
    surfaceBorder: "rgba(0, 0, 0, 0.10)",
    pageBg: "#f9fafb",
    headerBg: "#ffffff",
    sidebarBg: "#ffffff",
    textPrimary: "#111827",
    textSecondary: "#6B7280",
    textMuted: "#9CA3AF",
    border: "#e5e7eb",
    cardBg: "#ffffff",
    cardBorder: "#e5e7eb",
    cardShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    glassBlur: "none",
    inputBg: "#ffffff",
    inputBorder: "rgba(0, 0, 0, 0.10)",
    inputBorderHover: "rgba(0, 0, 0, 0.18)",
    inputShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
    focusRing: "0 0 0 3px rgba(52, 85, 211, 0.10)",
    focusBorder: "#3455D3",
    dropdownBg: "#ffffff",
    dropdownBorder: "rgba(0, 0, 0, 0.08)",
    dropdownShadow: "0 4px 16px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04)",
    hoverBg: "rgba(52, 85, 211, 0.06)",
    labelColor: "#374151",
    placeholderColor: "#9ca3af",
    disabledBg: "#f9fafb",
    disabledBorder: "rgba(0, 0, 0, 0.06)",
    disabledText: "#9ca3af",
  },
  dark: {
    surfaceBg: "rgba(16, 16, 16, 0.75)",
    surfaceBorder: "rgba(255, 255, 255, 0.15)",
    pageBg: "#0a0a1e",
    pageBgGradient:
      "linear-gradient(135deg, rgba(10, 10, 30, 0.95) 0%, rgba(20, 20, 50, 0.98) 100%)",
    headerBg: "rgba(16, 16, 16, 0.85)",
    sidebarBg: "rgba(16, 16, 16, 0.75)",
    textPrimary: "#DCDCDC",
    textSecondary: "#BCBCBC",
    textMuted: "#A0A0A0",
    border: "rgba(255, 255, 255, 0.15)",
    cardBg: "rgba(16, 16, 16, 0.75)",
    cardBorder: "rgba(255, 255, 255, 0.2)",
    cardShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.3)",
    // SECURITY: `glassBlur` MUST remain a build-time constant. If account-
    // level theming is ever added, DO NOT route user input into this
    // token without a strict allow-list sanitiser — `backdrop-filter:
    // <user-string>` becomes a CSS-injection vector (e.g. `url(...)`,
    // `image(...)`, or other CSS values that exfiltrate through paint).
    // The current value is exposed verbatim via `--verity-glass-blur` in
    // `main.tsx`'s cssVariablesResolver and consumed by DocsSidebar.
    glassBlur: "blur(30px) saturate(150%)",
    inputBg: "rgba(255, 255, 255, 0.05)",
    inputBorder: "rgba(255, 255, 255, 0.12)",
    inputBorderHover: "rgba(255, 255, 255, 0.20)",
    inputShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
    focusRing: "0 0 0 3px rgba(52, 85, 211, 0.15)",
    focusBorder: "rgba(52, 85, 211, 0.6)",
    dropdownBg: "rgba(20, 20, 25, 0.95)",
    dropdownBorder: "rgba(255, 255, 255, 0.10)",
    dropdownShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
    hoverBg: "rgba(255, 255, 255, 0.08)",
    labelColor: "#DCDCDC",
    placeholderColor: "#BCBCBC",
    disabledBg: "rgba(255, 255, 255, 0.02)",
    disabledBorder: "rgba(255, 255, 255, 0.06)",
    disabledText: "rgba(255, 255, 255, 0.3)",
  },
} as const;

// ─── Shared values ───────────────────────────────────────────────────────────
// Re-derived from the palette tuples above so a future palette tweak
// propagates to the flat scalars automatically. Don't hardcode hexes here.

export const accent = palette.blue[6];
export const errorColor = palette.error[6];
export const successColor = palette.success[6];
export const warningColor = palette.warning[6];

// ─── Typography ──────────────────────────────────────────────────────────────

export const fonts = {
  body: "Google Sans Flex, Google Sans, Helvetica Neue, sans-serif",
  heading: "Inter, sans-serif",
  mono: "JetBrains Mono, SFMono-Regular, Consolas, monospace",
  /** The dashboard edit sidebar (Arjen's design, 2026-10-10): Inter, else
   *  the system UI font (San Francisco on macOS, Segoe UI on Windows). */
  panel: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
} as const;

export const headingWeight = {
  h1: "600",
  h2: "600",
  h3: "600",
  h4: "500",
  h5: "500",
  h6: "500",
} as const;

// ─── Radii ───────────────────────────────────────────────────────────────────

export const radius = {
  sm: "6px",
  md: "8px",
  lg: "12px",
  xl: "16px",
} as const;

// ─── Transitions ─────────────────────────────────────────────────────────────

export const transition = {
  fast: "0.12s ease",
  normal: "0.2s ease",
  drawer: "280ms cubic-bezier(0.32, 0.72, 0, 1)",
} as const;
